import { ModalSignUpProps } from "domains/auth/components/ModalSignUp";
import { IS_PROD } from "domains/commons/environment";
import { ButtonProps } from "domains/ui/components/Button";

import { ImageProps } from "@chakra-ui/react";

export type HighlightCardType = {
  slug: string;
  tag?: string;
  title: string;
  description: string;
  background: ImageProps["src"];
  video?: ImageProps["src"];
  altText: string;
  altIntl: string;
  callToAction: {
    label: string;
    externalLink?: ButtonProps["externalLink"];
    internalLink?: ButtonProps["internalLink"];
    onClick?: "openChat" | "openCreateModelModal";
    signedVariant?: ModalSignUpProps["variant"];
  };
};

/**
 * Raw highlight cards in the order they should be displayed
 */
export const HIGHLIGHT_CARDS: HighlightCardType[] = [
  {
    slug: "introducing-3d",
    title: "Introducing 3D",
    tag: "New Tool",
    description:
      "Convert your images into high-fidelity 3D models with PBR textures. Export-ready in minutes.",
    callToAction: {
      label: "Try Now",
      //// @TODO Here we miss the random prompt selected from pinned examples
      internalLink: "/3d/new",
      signedVariant: "generate",
    },
    background: "/home/highlights/introducing_3d_640.webp",
    video: "/home/highlights/introducing_3d_640.mp4",
    altText:
      "Transform any of your images into a 3D model with Scenario's AI-powered 3D model generator. This tool allows you to create 3D models from 2D images, making it easy to bring your ideas to life in 3D.",
    altIntl: "home.highlights.introducing_3d.altText",
  },

  {
    slug: "edit-with-gpt",
    title: "Edit with GPT",
    tag: "New Tool",
    description:
      "Transform your visuals with leading editing models: <br/> Kontext, OpenAI GPT, Google Gemini",
    callToAction: {
      label: "Edit Now",
      internalLink: "/edit-with-prompts",
      signedVariant: "edit",
    },
    background: "/home/highlights/edit_with_gpt_640.webp",
    altText:
      "Image being modified using Scenario's GPT-powered editing tool, demonstrating AI prompt-based image editing for creative and precise visual transformation.",
    altIntl: "home.highlights.edit_with_gpt.altText",
  },

  {
    slug: "introducing-video",
    title: "Introducing Video",
    tag: "New Tool",
    description:
      "Create consistent videos from text or images, with intuitive controls and automated prompting",
    callToAction: {
      label: "Try Now",
      //// @TODO Here we miss the random prompt selected from pinned examples
      internalLink: "/videos/new",
      signedVariant: "generate",
    },
    background: "/home/highlights/introducing_video_640.webp",
    video: "/home/highlights/introducing_video_640.mp4",
    altText:
      "Preview of a magnificent tiger video generated from a still image with Scenario's AI video generation. With Scenario, you can create consistent videos from text or images with intuitive controls and automated prompts.",
    altIntl: "home.highlights.introducing_video.altText",
  },

  {
    slug: "ask-anything",
    title: "Ask Anything",
    tag: "Browse",
    description:
      "Get instant answers on Scenario tools and workflows,<br/>anywhere in the app",
    callToAction: {
      label: "Ask Now",
      onClick: "openChat",
    },
    background: "/home/highlights/ask_anything_640.webp",
    altText:
      "Screenshot of Scenario's AI-powered in-app assistant answering a user question, providing instant guidance on creative tools and workflows.",
    altIntl: "home.highlights.ask_anything.altText",
  },

  {
    slug: "get-inspired",
    title: "Get Inspired",
    tag: "Browse",
    description:
      "Explore top visuals, videos or AI models. Easily build upon<br/>your team's creations in your workspace",
    callToAction: {
      label: "Explore",
      internalLink: "/explore",
      signedVariant: "generate",
    },
    background: "/home/highlights/get_inspired_640.webp",
    video: "/home/highlights/get_inspired_640.mp4",
    altText:
      "Gallery of AI-generated images, videos, and models on Scenario's Explore page, showcasing top creative content and inspiring users to build on their team's creations.",
    altIntl: "home.highlights.get_inspired.altText",
  },

  {
    slug: "enterprise-grade-ai",
    title: "Enterprise-grade AI",
    tag: "Enterprise",
    description:
      "Built with SOC 2 compliance, robust security and privacy, and advanced features for data and user management",
    // NOT FITTING VERSION:
    // "Built for scale with SOC 2 compliance, robust security and privacy, and advanced features for data and user management",
    callToAction: {
      label: "Learn More",
      externalLink: "https://trust.scenario.com",
      signedVariant: "generate",
    },
    background: "/home/highlights/enterprise_grade_ai_640.webp",
    altText:
      "Illustration representing Scenario's enterprise-grade AI platform, highlighting SOC 2 compliance, privacy-focused content creation, and advanced security and user management features.",
    altIntl: "home.highlights.enterprise_grade_ai.altText",
  },

  {
    slug: "train-custom-styles",
    title: "Custom Styles",
    tag: "Tool",
    description:
      "Train custom AI models on your art for perfect<br/>consistency in style and characters",
    callToAction: {
      label: "Train Style",
      signedVariant: "generate",
      onClick: "openCreateModelModal",
    },
    background: "/home/highlights/train_custom_styles_640.webp",
    video: "/home/highlights/train_custom_styles_640.mp4",
    altText:
      "An artist visualizes a wall of their artwork once run through Scenario's training tool, resulting in consistently styled characters in every AI-generated image.",
    altIntl: "home.highlights.train_custom_styles.altText",
  },

  {
    slug: "all-in-one-platform",
    title: "All-in-one Platform",
    tag: "Tool",
    description:
      "Generate with the broadest range of models, from frontier to custom-trained, across all visual formats: 2D, video, and 3D",
    // NOT FITTING VERSION:
    // "Generate and scale with the broadest range of models, from frontier to custom-trained, across all visual formats: 2D, video, and 3D"
    callToAction: {
      label: "Generate",
      //// @TODO Here we miss the Random Prompt from pinned examples
      internalLink: {
        pathname: "/images/new",
        query: { modelId: "flux.1.1-pro-ultra" },
      },
      signedVariant: "generate",
    },
    background: "/home/highlights/all_in_one_640.webp",
    altText:
      "Collage of a 2D artwork, a video frame, and a 3D model created with Scenario, representing an all-in-one AI content generation platform spanning images, videos, and 3D.",
    altIntl: "home.highlights.all_in_one.altText",
  },

  {
    slug: "create-immersive-content",
    title: "Immersive Content",
    tag: "Learn",
    description:
      "Generate stunning 360° environments for creative workflows, production pipelines, and VR experiences",
    // NOT FITTING VERSION:
    // "Generate stunning 360° environments, stylized or photorealistic, for creative workflows, production pipelines, and VR experiences"
    callToAction: {
      label: "Learn More",
      externalLink: "https://x.com/emmanuel_2m/status/1931756594166993240",
    },
    background: "/home/highlights/create_immersive_content_640.webp",
    video: "/home/highlights/create_immersive_content_640.mp4",
    altText:
      "A panoramic 360-degree environment generated with Scenario's AI. The expressive and speaking yeti wears a woolly hat. This scene illustrates possibilities available on Scenario for immersive content creation for virtual reality and advanced creative workflows.",
    altIntl: "home.highlights.create_immersive_content.altText",
  },

  {
    slug: "leverage-the-api",
    title: "Automate with our API",
    tag: "Learn",
    description:
      "Streamline and scale your creative workflows with seamless automation while maintaining quality and control",
    // PROPOSED VERSION: used all the space, seemed too long
    // "Automate and scale your creative workflows effortlessly. Optimize production while maintaining quality and control."
    callToAction: {
      label: "Learn More",
      externalLink: "https://docs.scenario.com/",
    },
    background: "/home/highlights/leverage_the_api_640.webp",
    altText:
      "Illustration of Scenario's API automating AI content generation at scale, streamlining creative workflows while maintaining high quality and creative control.",
    altIntl: "home.highlights.leverage_the_api.altText",
  },

  {
    slug: "render-sketches",
    title: "Sketches-to-Render",
    tag: "New Tool",
    description:
      "From ideas to animated clips in 5 minutes. Render sketches<br/>into vibrant visuals, effortlessly",
    callToAction: {
      label: "Try Now",
      internalLink: "/edit-with-prompts",
      signedVariant: "generate",
    },
    background: "/home/highlights/render_sketches_640.webp",
    video: "/home/highlights/render_sketches_640.mp4",
    altText:
      "A rough sketch next to its fully rendered version generated by Scenario's Sketches-to-Render tool, demonstrating rapid AI-driven transformation from concept to polished art.",
    altIntl: "home.highlights.render_sketches.altText",
  },

  {
    slug: "precision-image-editing",
    title: "Precision Editing",
    tag: "Tool",
    description:
      "Retouch any part of an image with style-consistent inpainting,<br/>and achieve flawless edits, faster",
    callToAction: {
      label: "Retouch",
      internalLink: "/canvas/new",
      signedVariant: "generate",
    },
    background: "/home/highlights/precision_editing_640.webp",
    altText:
      "Photograph being retouched using Scenario's precision inpainting tool, as a selected area is seamlessly edited in matching style for fast, flawless AI-powered image refinement.",
    altIntl: "home.highlights.precision_editing.altText",
  },

  {
    slug: "create-with-veo3",
    title: "Create With Veo 3",
    tag: "Tool",
    description:
      "The world-class video model, now on Scenario: turning your images into videos with built-in audio",
    callToAction: {
      label: "Try Now",
      //// @TODO Here we miss the random prompt generation
      internalLink: {
        pathname: "/videos/new",
        query: IS_PROD
          ? { modelId: "model_veo3" }
          : { modelId: "model_staging_veo3" },
      },
      signedVariant: "generate",
    },
    background: "/home/highlights/create_with_veo3_640.webp",
    video: "/home/highlights/create_with_veo3_640.mp4",
    altText:
      "Animated scene generated from a still image with Scenario's Veo 3 AI video model, turning static art into dynamic video with sound and full creative control.",
    altIntl: "home.highlights.create_with_veo3.altText",
  },
];
